import { type UseQueryResult, useQueries } from "@tanstack/react-query"
import type { Omit } from "@wagmi/core/internal"
import { useMemo } from "react"
import { Evaluate } from "viem"
import { useChainId, useConfig } from "wagmi"
import {
  type GetAccountEnabledVaultsInfoErrorType,
  getAccountEnabledVaultsInfo,
} from "../actions/getAccountEnabledVaultsInfo"
import {
  type GetAccountEnabledVaultsInfoData,
  type GetAccountEnabledVaultsInfoOptions,
  type GetAccountEnabledVaultsInfoQueryFnData,
  type GetAccountEnabledVaultsInfoQueryKey,
  getAccountEnabledVaultsInfoQueryKey,
} from "../query"
import type {
  ConfigParameter,
  ExtendedConfig,
  UseQueryParameters,
} from "../types"

export type UseAccountEnabledVaultsInfoProps<
  config extends ExtendedConfig = ExtendedConfig,
  selectedData = GetAccountEnabledVaultsInfoData,
  combineData = UseQueryResult<selectedData>[],
> = ConfigParameter<config> & {
  queries: Evaluate<
    GetAccountEnabledVaultsInfoOptions<config> &
      ConfigParameter<config> & {
        query?:
          | Evaluate<
              Omit<
                UseQueryParameters<
                  GetAccountEnabledVaultsInfoQueryFnData,
                  GetAccountEnabledVaultsInfoErrorType,
                  selectedData,
                  GetAccountEnabledVaultsInfoQueryKey<config>
                >,
                "gcTime" | "staleTime"
              >
            >
          | undefined
      }
  >[]
  select?: (data: GetAccountEnabledVaultsInfoData) => selectedData
  combine?: (result: UseQueryResult<selectedData>[]) => combineData
}

export function useAccountEnabledVaultsInfo<
  config extends ExtendedConfig,
  selectedData = GetAccountEnabledVaultsInfoData,
  combineData = UseQueryResult<selectedData>[],
>(
  parameters: UseAccountEnabledVaultsInfoProps<
    config,
    selectedData,
    combineData
  >,
): combineData {
  const { queries = [], select, combine } = parameters
  const config = useConfig(parameters)
  const chainId = useChainId({ config })
  const queryResults = useQueries({
    queries: queries.map(({ query = {}, ...options }) => ({
      ...query,
      queryKey: getAccountEnabledVaultsInfoQueryKey({
        ...options,
        chainId: options.chainId || chainId,
      }),
      queryFn: () =>
        getAccountEnabledVaultsInfo(config, {
          ...options,
          chainId: options.chainId || chainId,
        }),
      select: select,
      refetchOnMount: false,
      retryOnMount: false,
      retry: false,
      enabled: Boolean(options.account && (query.enabled ?? true)),
    })),
  }) satisfies UseQueryResult<selectedData>[]

  return useMemo(
    () =>
      combine
        ? combine(queryResults)
        : (queryResults as unknown as combineData),
    [queryResults, combine],
  )
}
